<template>
  <div class="kinetic-energy-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$W_net = ΔEk = ½mv² - ½mv₀²$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <pattern id="grid-ground" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" stroke-width="1"/>
             </pattern>
             <filter id="glow-energy"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <rect width="400" height="240" fill="url(#grid-ground)" />
          
          <!-- The Track -->
          <line x1="20" y1="180" x2="380" y2="180" stroke="#334155" stroke-width="3" stroke-linecap="round" />
          <g v-for="i in 10" :key="'m'+i" :transform="`translate(${20 + i*36}, 185)`">
             <line y1="0" y2="5" stroke="#475569" stroke-width="1" />
          </g>

          <!-- The Sliding Block -->
          <g :transform="`translate(${pos}, 150)`">
             <rect x="-22" y="0" width="44" height="30" fill="#1e293b" stroke="#38bdf8" stroke-width="2" rx="4" />
             <rect x="-18" y="4" width="36" height="22" fill="rgba(56, 189, 248, 0.1)" rx="2" />
             
             <!-- Work Interaction Glow -->
             <rect v-if="isMoving && force > 0" x="-25" y="-3" width="50" height="36" fill="none" stroke="#ef4444" stroke-width="1.5" stroke-dasharray="4,2" filter="url(#glow-energy)">
                <animate attributeName="stroke-dashoffset" from="0" to="20" dur="0.5s" repeatCount="indefinite" />
             </rect>

             <!-- Force Arrow F -->
             <g v-if="force > 0" transform="translate(-50, 15)">
                <line x1="0" y1="0" x2="22" y2="0" stroke="#ef4444" stroke-width="3" marker-end="url(#arr-f-red)" />
                <text x="-5" y="-8" fill="#ef4444" font-size="11" font-weight="900">恒力 F</text>
             </g>

             <text y="48" text-anchor="middle" fill="#94a3b8" font-size="10" font-weight="bold">v = {{ v.toFixed(1) }} m/s</text>
          </g>

          <!-- Dynamic Energy Analysis Bar -->
          <g transform="translate(60, 40)">
             <text x="-45" y="10" fill="#ef4444" font-size="10" font-weight="900">作功 W</text>
             <rect x="0" y="0" width="280" height="12" fill="rgba(30, 41, 59, 0.8)" rx="6" />
             <rect x="0" y="0" :width="Math.min(280, (work / 200) * 280)" height="12" fill="#ef4444" rx="6" filter="url(#glow-energy)" />
             
             <text x="-45" y="32" fill="#10b981" font-size="10" font-weight="900">动能 Ek</text>
             <rect x="0" y="22" width="280" height="12" fill="rgba(30, 41, 59, 0.8)" rx="6" />
             <rect x="0" y="22" :width="Math.min(280, (ek_value / 200) * 280)" height="12" fill="#10b981" rx="6" filter="url(#glow-energy)" />
             
             <line v-if="isMoving" :x1="Math.min(280, (work / 200) * 280)" y1="5" :x2="Math.min(280, (ek_value / 200) * 280)" y2="28" stroke="rgba(255,255,255,0.4)" stroke-width="1" stroke-dasharray="2,2" />
          </g>

          <defs>
             <marker id="arr-f-red" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                <path d="M0,0 L6,2 L0,4 Z" fill="#ef4444" />
             </marker>
          </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>合外力功 W_net</label>
              <span class="val">{{ work.toFixed(1) }} <small>J</small></span>
           </div>
           <div class="hud-item green">
              <label>末动能 Ek</label>
              <span class="val">{{ ek_value.toFixed(1) }} <small>J</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>恒力强度 F (N)</label>
                 <el-slider v-model="force" :min="0" :max="10" :step="0.5" size="small"></el-slider>
              </div>
              <div class="s-item flex flex-row items-center justify-center">
                 <el-button type="primary" size="small" @click="start" icon="el-icon-video-play">启动加速</el-button>
                 <el-button size="small" @click="reset" icon="el-icon-refresh-left">归零</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>能量守恒启示：</strong>动能定理揭示了力在空间上的积累效应。你可以看到，每当红色的“作功条”增长时，绿色的“动能条”会**完全同步**地增长（忽略摩擦）。这说明**合外力所做的功完全转化为了物体动能的增量**。无论运动轨迹如何复杂，只要知道初末速度和总功，能量守恒始终是我们最坚实的物理依托。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KineticEnergyTheoremViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      force: 2.5,
      mass: 1.0,
      pos: 50,
      v: 0,
      work: 0,
      isMoving: false,
      timer: null
    }
  },
  computed: {
    ek_value() { return 0.5 * this.mass * this.v * this.v * 5; } // Scaling for visualization consistency
  },
  methods: {
    start() {
      if (this.isMoving) this.reset();
      this.isMoving = true;
      this.animate();
    },
    reset() {
      this.pos = 50;
      this.v = 0;
      this.work = 0;
      this.isMoving = false;
      if (this.timer) cancelAnimationFrame(this.timer);
    },
    animate() {
      if (!this.isMoving) return;
      
      const dt = 0.016;
      const a = this.force / this.mass;
      const ds = this.v * dt + 0.5 * a * dt * dt;
      
      this.v += a * dt;
      this.pos += this.v * 2.5; 
      
      this.work += this.force * ds * 5; 

      if (this.pos > 370 || this.work > 195) {
        this.isMoving = false;
      } else {
        this.timer = requestAnimationFrame(this.animate);
      }
    }
  },
  beforeDestroy() { if (this.timer) cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.kinetic-energy-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 6px 12px; label { display: block; font-size: 9px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #0ea5e9; } &.green { border-left: 3px solid #10b981; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.flex { display: flex; } .items-center { align-items: center; } .justify-center { justify-content: center; }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: flex-start; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
